import React, { useState } from 'react';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TableSortLabel,
    IconButton,
    Box,
    Typography,
    Tooltip
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { formatWeekHeader, isDatePeriod } from '../Utilities/weekNumberUtils';

interface ForecastPeriod {
    forecastPeriodId: number;
    periodIndex: number;
    periodName: string;
}

interface Forecast {
    forecastId: number;
    name: string;
    startDate: string;
    forecastPeriods: ForecastPeriod[];
}

interface PeriodData {
    forecast: number | null;
    actual: number | null;
    variance: number | null;
    absoluteVariance: number | null;
}

interface ChildRow {
    id: string;
    customerName?: string;
    sku?: string;
    description?: string;
    periods: PeriodData[];
}

interface AccuracyRow {
    id: string;
    customerName?: string;
    sku?: string;
    description?: string;
    periods: PeriodData[];
    children?: ChildRow[];
}

interface ForecastAccuracyData {
    periodNames: string[];
    rows: AccuracyRow[];
}

interface ForecastAccuracyGridProps {
    data: ForecastAccuracyData | null;
    selectedForecast?: Forecast;
    aggregationPeriod: 'monthly' | 'quarterly' | 'weekly';
    viewMode: 'revenue' | 'units';
    groupBy: 'customer' | 'sku';
    varianceMode: 'percent' | 'absolute';
}

type SortOrder = 'asc' | 'desc';
type OrderByField = 'customerName' | 'sku' | 'description' | 'fy';

const ForecastAccuracyGrid: React.FC<ForecastAccuracyGridProps> = ({ 
    data, 
    selectedForecast, 
    aggregationPeriod, 
    viewMode, 
    groupBy, 
    varianceMode 
}) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [orderBy, setOrderBy] = useState<OrderByField>('fy');
    const [order, setOrder] = useState<SortOrder>('desc');
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

    if (!data || !data.rows || data.rows.length === 0) {
        return (
            <Paper sx={{ p: 3 }}>
                <Typography>No data available</Typography>
            </Paper>
        );
    }

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleRequestSort = (property: OrderByField) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleRowExpand = (rowId: string) => {
        const newExpanded = new Set(expandedRows);
        if (newExpanded.has(rowId)) {
            newExpanded.delete(rowId);
        } else {
            newExpanded.add(rowId);
        }
        setExpandedRows(newExpanded);
    };

    const formatValue = (value: number | null | undefined): string => {
        if (value === null || value === undefined) return '-';
        if (viewMode === 'revenue') {
            return `£${value.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
        }
        return value.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    };

    const formatVariance = (variance: number | null | undefined, absoluteVariance: number | null | undefined): string => {
        if (varianceMode === 'absolute') {
            if (absoluteVariance === null || absoluteVariance === undefined) return '-';
            const formatted = viewMode === 'revenue'
                ? `£${Math.abs(absoluteVariance).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
                : Math.abs(absoluteVariance).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
            return absoluteVariance >= 0 ? `+${formatted}` : `-${formatted}`;
        } else {
            if (variance === null || variance === undefined) return '-';
            const formatted = Math.abs(variance).toFixed(1);
            return variance >= 0 ? `+${formatted}%` : `-${formatted}%`;
        }
    };

    const getVarianceColor = (variance: number | null | undefined, absoluteVariance: number | null | undefined): string => {
        const value = varianceMode === 'absolute' ? absoluteVariance : variance;
        if (value === null || value === undefined) return 'text.secondary';
        if (value >= 0) return 'success.main';
        return 'error.main';
    };

    const rawPeriodNames = data.periodNames || [];
    const basePeriodNames = rawPeriodNames.filter(name => name !== 'FY');
    
    const periodNamesWithFY = [...basePeriodNames, 'FY'];

    const periodNames = aggregationPeriod === "weekly" 
        ? periodNamesWithFY.map((period, index) => {
            if (/^\d{2}-\d{2}-\d{2,4}$/.test(period)) {
                return formatWeekHeader(period, index);
            }
            return period;
        })
        : periodNamesWithFY;

    const sortData = (rows: AccuracyRow[]): AccuracyRow[] => {
        return [...rows].sort((a, b) => {
            let aValue: string | number;
            let bValue: string | number;

            if (orderBy === 'fy') {
                const aFyPeriod = a.periods[a.periods.length - 1];
                const bFyPeriod = b.periods[b.periods.length - 1];
                aValue = (viewMode === 'revenue' ? aFyPeriod?.actual : aFyPeriod?.actual) || 0;
                bValue = (viewMode === 'revenue' ? bFyPeriod?.actual : bFyPeriod?.actual) || 0;
            } else {
                aValue = (a as any)[orderBy] || '';
                bValue = (b as any)[orderBy] || '';

                if (orderBy === 'customerName' || orderBy === 'sku' || orderBy === 'description') {
                    aValue = (aValue as string).toLowerCase();
                    bValue = (bValue as string).toLowerCase();
                } else {
                    aValue = aValue || 0;
                    bValue = bValue || 0;
                }
            }

            if (aValue < bValue) {
                return order === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return order === 'asc' ? 1 : -1;
            }
            return 0;
        });
    };

    const sortedRows = sortData(data.rows);
    const paginatedRows = sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const copyToClipboard = async () => {
        if (!data || !data.rows || data.rows.length === 0) return;

        let csvContent = '';
        
        const headerRow = groupBy === 'customer' ? ['Customer Name'] : ['SKU', 'Description'];
        
        periodNames.forEach(period => {
            const periodLabel = period.replace('\n', ' ');
            headerRow.push(`${periodLabel} Forecast`);
            headerRow.push(`${periodLabel} Actual`);
            headerRow.push(`${periodLabel} Difference`);
        });
        
        csvContent += headerRow.join('\t') + '\n';

        const processRow = (row: AccuracyRow | ChildRow, isChild = false): string => {
            const rowData: string[] = [];
            
            if (groupBy === 'customer') {
                if (isChild) {
                    const sku = (row as ChildRow).sku || '';
                    const description = (row as ChildRow).description || '';
                    rowData.push(`${sku} - ${description}`);
                } else {
                    rowData.push((row as AccuracyRow).customerName || '');
                }
            } else {
                if (isChild) {
                    rowData.push('');
                    rowData.push((row as ChildRow).customerName || '');
                } else {
                    rowData.push((row as AccuracyRow).sku || '');
                    rowData.push((row as AccuracyRow).description || '');
                }
            }

            const periods = row.periods || [];
            const basePeriodCount = periods.length - 1;
            
            for (let i = 0; i < basePeriodCount; i++) {
                const period = periods[i];
                if (period) {
                    rowData.push(period.forecast?.toFixed(2) || '');
                    rowData.push(period.actual?.toFixed(2) || '');
                    rowData.push(period.absoluteVariance?.toFixed(2) || '');
                } else {
                    rowData.push('', '', '');
                }
            }

            const fyPeriod = periods[basePeriodCount];
            if (fyPeriod) {
                rowData.push(fyPeriod.forecast?.toFixed(2) || '');
                rowData.push(fyPeriod.actual?.toFixed(2) || '');
                rowData.push(fyPeriod.absoluteVariance?.toFixed(2) || '');
            } else {
                rowData.push('', '', '');
            }

            return rowData.join('\t');
        };

        sortedRows.forEach(row => {
            csvContent += processRow(row, false) + '\n';
            
            if (row.children && row.children.length > 0) {
                row.children.forEach(child => {
                    csvContent += processRow(child, true) + '\n';
                });
            }
        });

        try {
            await navigator.clipboard.writeText(csvContent);
        } catch (err) {
            console.error('Failed to copy to clipboard:', err);
        }
    };

    const renderPeriodCell = (periodData: PeriodData | null) => {
        if (!periodData) return <TableCell align="right">-</TableCell>;
        
        return (
            <TableCell align="right">
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.2 }}>
                    <Typography
                        variant="caption"
                        component="span"
                        color="text.secondary"
                        sx={{ display: 'block', whiteSpace: 'nowrap', fontSize: '0.9rem' }}
                    >
                        {formatValue(periodData.forecast)}
                    </Typography>
                    <Typography 
                        variant="body2"
                        sx={{ color: 'primary.main', fontWeight: 'bold' }}
                    >
                        {formatValue(periodData.actual)}
                    </Typography>
                    <Typography
                        variant="caption"
                        component="span"
                        sx={{ display: 'block', whiteSpace: 'nowrap', color: getVarianceColor(periodData.variance, periodData.absoluteVariance) }}
                    >
                        {formatVariance(periodData.variance, periodData.absoluteVariance)}
                    </Typography>
                </Box>
            </TableCell>
        );
    };

    const renderRow = (row: AccuracyRow | ChildRow, isChild = false) => {
        const isExpanded = expandedRows.has(row.id);
        const hasChildren = 'children' in row && row.children && row.children.length > 0 && !isChild;

        const periods = row.periods || [];
        const basePeriodCount = periods.length - 1;
        const basePeriods = periods.slice(0, basePeriodCount);
        const fyPeriod = periods[basePeriodCount];

        return (
            <TableRow 
                key={row.id}
                sx={{ 
                    backgroundColor: isChild ? 'rgba(0, 0, 0, 0.02)' : 'inherit',
                    cursor: hasChildren ? 'pointer' : 'default',
                    '&:hover': hasChildren ? {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    } : {}
                }}
                onClick={hasChildren ? () => handleRowExpand(row.id) : undefined}
            >
                <TableCell sx={{ width: 50, pl: isChild ? 2 : 0.75 }}>
                    {hasChildren && (
                        <IconButton
                            aria-label="expand row"
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleRowExpand(row.id);
                            }}
                            sx={{ p: 0 }}
                        >
                            {isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                        </IconButton>
                    )}
                </TableCell>
                {groupBy === 'customer' && (
                    <TableCell component="th" scope="row" sx={{ pl: isChild ? 4 : 2 }} colSpan={2}>
                        {isChild ? `${(row as ChildRow).sku} - ${(row as ChildRow).description}` : (row as AccuracyRow).customerName}
                    </TableCell>
                )}
                {groupBy === 'sku' && (
                    <>
                        <TableCell component="th" scope="row" sx={{ pl: isChild ? 4 : 2 }} colSpan={isChild ? 2 : 1}>
                            {isChild ? (row as ChildRow).customerName : (row as AccuracyRow).sku}
                        </TableCell>
                        {!isChild && <TableCell>{(row as AccuracyRow).description}</TableCell>}
                    </>
                )}
                {basePeriods.map((periodData, index) => (
                    <React.Fragment key={index}>
                        {renderPeriodCell(periodData)}
                    </React.Fragment>
                ))}
                {fyPeriod && renderPeriodCell(fyPeriod)}
            </TableRow>
        );
    };

    const renderRows = () => {
        const rows: JSX.Element[] = [];
        paginatedRows.forEach((row) => {
            rows.push(renderRow(row, false));
            
            if (row.children && row.children.length > 0 && expandedRows.has(row.id)) {
                row.children.forEach((child) => {
                    rows.push(renderRow(child, true));
                });
            }
        });
        return rows;
    };

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Paper elevation={1} sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
                <Tooltip title="Copy table to clipboard">
                    <IconButton
                        onClick={copyToClipboard}
                        size="small"
                        sx={{ p: 0.5 }}
                    >
                        <ContentCopyIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Paper>
            <TableContainer 
                component={Paper}
                sx={{ 
                    height: '100%',
                    overflow: 'auto',
                    maxWidth: '100%',
                    display: 'inline-block',
                    width: 'fit-content',
                    minWidth: '100%',
                    flexGrow: 1
                }}
            >
                <Table 
                    stickyHeader 
                    aria-label="forecast accuracy table"
                    sx={{ 
                        tableLayout: 'auto', 
                        width: 'auto',
                        '& .MuiTableBody-root .MuiTableCell-root': {
                            whiteSpace: 'nowrap'
                        },
                        '& .MuiTableHead-root .MuiTableCell-root': {
                            whiteSpace: 'nowrap'
                        },
                        '& .MuiTableHead-root .MuiTableCell-root[data-week-header="true"]': {
                            whiteSpace: 'pre'
                        }
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ 
                                width: 50,
                                backgroundColor: (theme) => theme.palette.background.paper,
                                zIndex: 1
                            }} />
                            {groupBy === 'customer' && (
                                <TableCell colSpan={2} sx={{
                                    backgroundColor: (theme) => theme.palette.background.paper,
                                    zIndex: 1
                                }}>
                                    <TableSortLabel
                                        active={orderBy === 'customerName'}
                                        direction={orderBy === 'customerName' ? order : 'asc'}
                                        onClick={() => handleRequestSort('customerName')}
                                    >
                                        Customer Name
                                    </TableSortLabel>
                                </TableCell>
                            )}
                            {groupBy === 'sku' && (
                                <>
                                    <TableCell sx={{
                                        backgroundColor: (theme) => theme.palette.background.paper,
                                        zIndex: 1
                                    }}>
                                        <TableSortLabel
                                            active={orderBy === 'sku'}
                                            direction={orderBy === 'sku' ? order : 'asc'}
                                            onClick={() => handleRequestSort('sku')}
                                        >
                                            SKU
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell sx={{
                                        backgroundColor: (theme) => theme.palette.background.paper,
                                        zIndex: 1
                                    }}>
                                        <TableSortLabel
                                            active={orderBy === 'description'}
                                            direction={orderBy === 'description' ? order : 'asc'}
                                            onClick={() => handleRequestSort('description')}
                                        >
                                            Description
                                        </TableSortLabel>
                                    </TableCell>
                                </>
                            )}
                            {periodNames.map((period, index) => {
                                const isWeekHeader = aggregationPeriod === "weekly" && isDatePeriod(period);
                                const isFyColumn = period === 'FY';
                                return (
                                    <TableCell
                                        key={index}
                                        align="right"
                                        sx={{
                                            whiteSpace: isWeekHeader ? 'pre' : 'nowrap',
                                            backgroundColor: (theme) => theme.palette.background.paper,
                                            zIndex: 1
                                        }}
                                        data-week-header={isWeekHeader}
                                    >
                                        {isFyColumn ? (
                                            <TableSortLabel
                                                active={orderBy === 'fy'}
                                                direction={orderBy === 'fy' ? order : 'desc'}
                                                onClick={() => handleRequestSort('fy')}
                                            >
                                                {period.toUpperCase()}
                                            </TableSortLabel>
                                        ) : (
                                            period.toUpperCase()
                                        )}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {renderRows()}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={sortedRows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{ flexShrink: 0 }}
            />
        </Box>
    );
};

export default ForecastAccuracyGrid;

